import { useCallback, useEffect, useRef, useState } from 'react';
import type { Page } from '../types/api';
import { useDebouncedValue } from './useDebouncedValue';

interface Args<T, P> {
  fetcher: (params: { page: number; size: number; q: string; filters: P }) => Promise<Page<T>>;
  initialFilters: P;
  pageSize?: number;
}

export function usePagedQuery<T, P>({ fetcher, initialFilters, pageSize = 25 }: Args<T, P>) {
  const [page, setPage] = useState(0);
  const [q, setQ] = useState('');
  const [filters, setFilters] = useState<P>(initialFilters);
  const [data, setData] = useState<T[]>([]);
  const [totalCount, setTotalCount] = useState<number | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const ticket = useRef(0);

  const debouncedQ = useDebouncedValue(q, 300);

  useEffect(() => { setPage(0); }, [debouncedQ, filters]);

  const load = useCallback(async () => {
    const my = ++ticket.current;
    setLoading(true);
    setError(null);
    try {
      const res = await fetcher({ page, size: pageSize, q: debouncedQ, filters });
      if (ticket.current === my) {
        setData(res.data);
        setTotalCount(res.totalCount);
      }
    } catch (err) {
      if (ticket.current === my) setError((err as Error).message);
    } finally {
      if (ticket.current === my) setLoading(false);
    }
  }, [fetcher, page, pageSize, debouncedQ, filters]);

  useEffect(() => { load(); }, [load]);

  return {
    data, page, setPage, q, setQ, filters, setFilters,
    loading, error, totalCount, pageSize,
    refresh: load,
  };
}
